// ABOUTME: Log of one job attempt with a level filter; while the attempt is live it tails new
// ABOUTME: entries and keeps the view pinned to the bottom unless the user scrolled up.

import {
    Backdrop,
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    Stack,
    Typography,
} from '@ds/coolshop';
import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAttemptLogs } from '../../../hooks/useAttemptLogs/useAttemptLogs';
import { LogLevel } from '../../../types';

const LEVELS: LogLevel[] = ['info', 'warn', 'error'];

const levelMessages = defineMessages({
    info: { id: 'job.logs.level.info', defaultMessage: 'Info' },
    warn: { id: 'job.logs.level.warn', defaultMessage: 'Warning' },
    error: { id: 'job.logs.level.error', defaultMessage: 'Error' },
});

const LEVEL_COLORS: Record<LogLevel, string> = {
    info: 'text.secondary',
    warn: 'warning.main',
    error: 'error.main',
};

type Props = { jobId: string; attempt: number; live: boolean };

export function AttemptLogs(props: Readonly<Props>) {
    const { jobId, attempt, live } = props;
    const intl = useIntl();
    const [levels, setLevels] = useState<LogLevel[]>(LEVELS);
    // Every level selected is the same as no filter: the server is not asked to filter then.
    const filter = levels.length === LEVELS.length ? undefined : levels;
    const { entries, loading, error } = useAttemptLogs(jobId, attempt, filter, live);
    const scroller = useRef<HTMLDivElement>(null);
    const pinned = useRef(true);

    useEffect(() => {
        const element = scroller.current;
        if (element && pinned.current) {
            element.scrollTop = element.scrollHeight;
        }
    }, [entries.length]);

    const toggleLevel = (level: LogLevel, checked: boolean) =>
        setLevels(prev =>
            checked
                ? LEVELS.filter(l => l === level || prev.includes(l))
                : prev.filter(l => l !== level),
        );

    return (
        // Takes the height left by the result sections, never less than a readable minimum.
        <Stack gap={1} flex={1} minHeight={240}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Box flex={1} display="flex" alignItems="center" gap={1}>
                    <Typography color="primary" variant="h6">
                        <b>
                            {intl.formatMessage({ id: 'job.logs.title', defaultMessage: 'Logs' })}
                        </b>
                    </Typography>
                    {/* Which attempt these lines belong to: the logs follow the selected one. */}
                    <Chip
                        size="small"
                        label={intl.formatMessage(
                            { id: 'job.attempt.tab', defaultMessage: 'Attempt {n}' },
                            { n: attempt },
                        )}
                    />
                </Box>
                {LEVELS.map(level => (
                    <FormControlLabel
                        key={level}
                        control={
                            <Checkbox
                                size="small"
                                checked={levels.includes(level)}
                                // At least one level stays selected.
                                disabled={levels.length === 1 && levels.includes(level)}
                                onChange={event => toggleLevel(level, event.target.checked)}
                            />
                        }
                        label={intl.formatMessage(levelMessages[level])}
                    />
                ))}
                {live && (
                    <Chip
                        size="small"
                        color="primary"
                        label={intl.formatMessage({ id: 'job.logs.live', defaultMessage: 'Live' })}
                    />
                )}
            </Box>
            {error && (
                <Typography variant="body2" color="error">
                    {error}
                </Typography>
            )}
            {/* The loader sits over the log, centred: the lines and the filters never move. */}
            <Box position="relative" display="flex" flex={1} minHeight={0}>
                <Backdrop open={loading} position="absolute" zIndex={1} invisible>
                    <CircularProgress />
                </Backdrop>
                <Box
                    ref={scroller}
                    flex={1}
                    minHeight={0}
                    overflowY="auto"
                    border="1px solid"
                    borderColor="divider"
                    borderRadius={1}
                    p={1}
                    bgcolor="background.default"
                    onScroll={() => {
                        const element = scroller.current;
                        if (!element) return;
                        pinned.current =
                            element.scrollHeight - element.scrollTop - element.clientHeight < 24;
                    }}
                >
                    {!entries.length && !loading ? (
                        <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({
                                id: 'job.logs.empty',
                                defaultMessage: 'No log entries.',
                            })}
                        </Typography>
                    ) : (
                        entries.map(entry => (
                            <Box key={entry.seq} display="flex" gap={1} fontFamily="monospace">
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    whiteSpace="nowrap"
                                >
                                    {entry.ts ? format(new Date(entry.ts), 'HH:mm:ss') : ''}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color={LEVEL_COLORS[entry.level] ?? 'text.secondary'}
                                    minWidth={40}
                                >
                                    {entry.level.toUpperCase()}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    whiteSpace="pre-wrap"
                                    wordBreak="break-word"
                                >
                                    {entry.message}
                                </Typography>
                            </Box>
                        ))
                    )}
                </Box>
            </Box>
        </Stack>
    );
}
